import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CATEGORIES, PRIORITIES, RECURRENCES, EVENT_TYPES } from "../lib/constants";
import { Sparkles, FileText, Plus, X, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const REMINDER_CHOICES = [30, 14, 7, 3, 1];

const empty = {
  title: "", description: "", category: "other", amount: "", currency: "",
  event_type: "other", event_date_label: "Date", due_date: "", action: "",
  priority: "medium", recurrence: "none", entity: "",
  reminder_offsets: [], sources: {}, source_document: null, file_id: null,
};

function SourceBadge({ source, hasDoc }) {
  const { t } = useTranslation();
  if (!source) return null;
  const map = {
    extracted: { label: hasDoc ? t("review.badgeFromDoc") : t("review.badgeFromNote"), cls: "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900" },
    inferred: { label: t("review.badgeInferred"), cls: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900" },
    suggested: { label: t("review.badgeSuggested"), cls: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900" },
  };
  const s = map[source];
  if (!s) return null;
  return <span data-testid={`source-badge-${source}`} className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${s.cls}`}>{s.label}</span>;
}

export default function ReviewModal({ extracted, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(empty);
  const [suggested, setSuggested] = useState([]);
  const [customOpen, setCustomOpen] = useState(false);
  const [customVal, setCustomVal] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (extracted) {
      setForm({
        ...empty, ...extracted,
        due_date: extracted.event_date || extracted.due_date || "",
        event_date_label: extracted.event_date_label || "Date",
        amount: extracted.amount ?? "",
        currency: extracted.currency || "",
        entity: extracted.entity || "",
        reminder_offsets: extracted.reminder_offsets || [],
        sources: extracted.sources || {},
      });
      setSuggested((extracted.suggested_reminders || []).filter((v) => !(extracted.reminder_offsets || []).includes(v)));
      setCustomOpen(false); setCustomVal("");
    }
  }, [extracted]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const hasDoc = !!form.source_document;
  const src = (field) => form.sources?.[field];
  const dateLabel = t(`eventTypes.${form.event_type || "other"}`);

  const toggleReminder = (val) => {
    setForm((f) => {
      const has = f.reminder_offsets.includes(val);
      return { ...f, reminder_offsets: has ? f.reminder_offsets.filter((v) => v !== val) : [...f.reminder_offsets, val] };
    });
  };

  const addCustom = () => {
    const n = parseInt(customVal);
    if (!isNaN(n) && n > 0 && !form.reminder_offsets.includes(n)) {
      set("reminder_offsets", [...form.reminder_offsets, n]);
    }
    setCustomVal(""); setCustomOpen(false);
  };

  const handleCancel = async () => {
    const fid = form.file_id;
    onClose();
    if (fid) {
      try { await api.delete(`/files/${fid}`); } catch (e) { /* orphan cleanup best-effort */ }
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error(t("review.titleRequired")); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description, category: form.category,
        amount: form.amount === "" || form.amount === null ? null : Number(form.amount),
        currency: form.currency || null,
        due_date: form.due_date || null,
        event_type: form.event_type, event_date_label: form.event_date_label,
        action: form.action, priority: form.priority, recurrence: form.recurrence,
        entity: form.entity || null,
        source_document: form.source_document, file_id: form.file_id,
        reminder_offsets: [...form.reminder_offsets].sort((a, b) => b - a),
        sources: form.sources || {},
      };
      await api.post("/items", payload);
      toast.success(t("review.saved"));
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const chipValues = Array.from(new Set([...REMINDER_CHOICES, ...form.reminder_offsets, ...suggested])).sort((a, b) => b - a);

  return (
    <Dialog open={!!extracted} onOpenChange={(v) => !v && handleCancel()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="review-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles className="h-5 w-5 brand-text" />
            {t("review.title")}
          </DialogTitle>
          <DialogDescription>{t("review.description")}</DialogDescription>
        </DialogHeader>

        {form.source_document && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary rounded-lg px-3 py-2">
            <FileText className="h-3.5 w-3.5" /> {form.source_document}
          </div>
        )}

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">{t("review.fieldTitle")}</Label>
            <Input data-testid="review-title" value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>

          <div>
            <Label className="text-xs">{t("review.summary")}</Label>
            <Textarea data-testid="review-description" value={form.description} rows={2}
              onChange={(e) => set("description", e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1.5">{t("review.category")} <SourceBadge source={src("category")} hasDoc={hasDoc} /></Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="review-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{t(`categories.${c.id}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <span data-testid="review-date-label">{dateLabel}</span>
                <SourceBadge source={src("event_date")} hasDoc={hasDoc} />
              </Label>
              <Input data-testid="review-due-date" type="date" value={form.due_date}
                onChange={(e) => set("due_date", e.target.value)} />
            </div>
          </div>

          <div>
            <Label className="text-xs">{t("review.typeOfDate")}</Label>
            <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
              <SelectTrigger data-testid="review-event-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EVENT_TYPES.map((et) => (
                  <SelectItem key={et} value={et}>{t(`eventTypes.${et}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1.5">{t("review.amount")} <SourceBadge source={src("amount")} hasDoc={hasDoc} /></Label>
              <Input data-testid="review-amount" type="number" value={form.amount}
                placeholder={t("common.optional")} onChange={(e) => set("amount", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1.5">{t("review.currency")} <SourceBadge source={src("currency")} hasDoc={hasDoc} /></Label>
              <Input data-testid="review-currency" value={form.currency} placeholder="—"
                onChange={(e) => set("currency", e.target.value)} />
            </div>
          </div>

          <div>
            <Label className="text-xs">{t("review.actionRequired")}</Label>
            <Input data-testid="review-action" value={form.action} onChange={(e) => set("action", e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1.5">{t("review.priority")} <SourceBadge source={src("priority")} hasDoc={hasDoc} /></Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="review-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(`priority.${p}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1.5">{t("review.recurrence")} <SourceBadge source={src("recurrence")} hasDoc={hasDoc} /></Label>
              <Select value={form.recurrence} onValueChange={(v) => set("recurrence", v)}>
                <SelectTrigger data-testid="review-recurrence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RECURRENCES.map((r) => <SelectItem key={r} value={r}>{t(`recurrence.${r}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div data-testid="reminder-selector">
            <Label className="text-xs">{t("review.reminderQuestion")}</Label>
            {form.reminder_offsets.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">{t("review.noReminders")}</p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {chipValues.map((val) => {
                const selected = form.reminder_offsets.includes(val);
                const isSuggested = !selected && suggested.includes(val);
                return (
                  <button key={val} type="button" data-testid={`reminder-chip-${val}`}
                    onClick={() => toggleReminder(val)}
                    className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors inline-flex items-center gap-1 ${
                      selected ? "brand-bg text-white border-transparent"
                        : isSuggested ? "border-sky-300 text-sky-700 bg-sky-50 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900"
                        : "border-border text-muted-foreground hover:border-brand hover:text-foreground"
                    }`}>
                    {selected && <Check className="h-3 w-3" />}
                    {t("reminders.daysBefore", { count: val })}
                    {isSuggested && <span className="text-[9px] uppercase opacity-70">· {t("reminders.suggested")}</span>}
                  </button>
                );
              })}

              {!customOpen ? (
                <button type="button" data-testid="reminder-add" onClick={() => setCustomOpen(true)}
                  className="text-xs px-3 py-1.5 rounded-full border border-dashed border-border text-muted-foreground hover:border-brand hover:text-foreground inline-flex items-center gap-1">
                  <Plus className="h-3 w-3" /> {t("review.addReminder")}
                </button>
              ) : (
                <div className="inline-flex items-center gap-1">
                  <Input data-testid="reminder-custom-input" autoFocus type="number" value={customVal}
                    onChange={(e) => setCustomVal(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addCustom()}
                    className="h-8 w-20 text-xs" placeholder={t("review.daysPlaceholder")} />
                  <Button type="button" size="sm" className="h-8 brand-bg text-white" data-testid="reminder-custom-add" onClick={addCustom}>{t("review.add")}</Button>
                  <button type="button" onClick={() => { setCustomOpen(false); setCustomVal(""); }} className="text-muted-foreground"><X className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
          <Button data-testid="btn-cancel-extraction" variant="ghost" onClick={handleCancel}>{t("common.cancel")}</Button>
          <Button data-testid="btn-confirm-extraction" onClick={save} disabled={saving}
            className="brand-bg text-white hover:opacity-90">
            {saving ? t("common.saving") : t("review.confirmSave")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
